import { Controller } from '@hotwired/stimulus'
import { i18n } from '../lib/i18n'

// Manages display format configuration for report data fields
// Provides both guided mode (type selector with options) and raw JSON mode
export default class extends Controller {
  static targets = ['type', 'options', 'hidden', 'guidedMode', 'jsonMode', 'jsonInput', 'modeLabel']
  static values = { initial: String }

  connect() {
    this.isJsonMode = false
    this.config = {}

    // Parse initial value
    if (this.initialValue) {
      try {
        this.config = JSON.parse(this.initialValue) || {}
      } catch (e) {
        this.config = {}
      }
    }

    // Set initial type selection
    if (this.config.type && this.hasTypeTarget) {
      this.typeTarget.value = this.config.type
    }

    this.renderOptions()
    this.syncHidden()
  }

  typeChanged() {
    this.config.type = this.typeTarget.value || null

    // Reset type-specific options when type changes
    if (!this.config.type) {
      this.config = {}
    } else {
      // Preserve type, reset other options
      this.config = { type: this.config.type }
    }

    this.renderOptions()
    this.syncHidden()
  }

  renderOptions() {
    if (!this.hasOptionsTarget) return

    const type = this.config.type
    let html = ''

    switch (type) {
      case 'currency':
        html = `
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="label py-0"><span class="label-text text-xs">Prefix</span></label>
              <input type="text"
                     class="input input-bordered input-sm w-full"
                     value="${this.escapeHtml(this.config.prefix || '$')}"
                     data-option="prefix"
                     data-action="input->format-config#updateOption">
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Decimals</span></label>
              <input type="number"
                     class="input input-bordered input-sm w-full"
                     value="${this.config.decimals ?? 2}"
                     min="0"
                     max="4"
                     data-option="decimals"
                     data-action="input->format-config#updateOption">
            </div>
          </div>
        `
        break

      case 'percentage':
        html = `
          <div>
            <label class="label py-0"><span class="label-text text-xs">Decimals</span></label>
            <input type="number"
                   class="input input-bordered input-sm w-32"
                   value="${this.config.decimals ?? 1}"
                   min="0"
                   max="4"
                   data-option="decimals"
                   data-action="input->format-config#updateOption">
          </div>
        `
        break

      case 'number':
        html = `
          <div>
            <label class="label py-0"><span class="label-text text-xs">Decimals</span></label>
            <input type="number"
                   class="input input-bordered input-sm w-32"
                   value="${this.config.decimals ?? 0}"
                   min="0"
                   max="4"
                   data-option="decimals"
                   data-action="input->format-config#updateOption">
          </div>
        `
        break

      case 'date':
        html = `
          <div>
            <label class="label py-0"><span class="label-text text-xs">Format</span></label>
            <select class="select select-bordered select-sm w-full"
                    data-option="format"
                    data-action="change->format-config#updateOption">
              <option value="short" ${this.config.format === 'short' ? 'selected' : ''}>Short (Jan 1, 2024)</option>
              <option value="long" ${this.config.format === 'long' ? 'selected' : ''}>Long (January 1, 2024)</option>
              <option value="iso" ${this.config.format === 'iso' ? 'selected' : ''}>ISO (2024-01-01)</option>
              <option value="relative" ${this.config.format === 'relative' ? 'selected' : ''}>Relative (2 days ago)</option>
            </select>
          </div>
        `
        break

      default:
        html = ''
    }

    this.optionsTarget.innerHTML = html
    this.optionsTarget.classList.toggle('hidden', !type)
  }

  updateOption(event) {
    const option = event.currentTarget.dataset.option
    let value = event.currentTarget.value

    // Convert numeric values
    if (option === 'decimals') {
      value = parseInt(value) || 0
    }

    this.config[option] = value
    this.syncHidden()
  }

  toggleMode() {
    this.isJsonMode = !this.isJsonMode

    if (this.isJsonMode) {
      // Switch to JSON mode
      this.guidedModeTarget.classList.add('hidden')
      this.jsonModeTarget.classList.remove('hidden')
      this.modeLabelTarget.textContent = i18n.t('format_config.show_guided')

      // Populate JSON textarea
      const hasConfig = Object.keys(this.config).length > 0
      this.jsonInputTarget.value = hasConfig ? JSON.stringify(this.config, null, 2) : ''
    } else {
      // Switch to guided mode
      this.guidedModeTarget.classList.remove('hidden')
      this.jsonModeTarget.classList.add('hidden')
      this.modeLabelTarget.textContent = i18n.t('format_config.show_json')

      // Parse JSON and update
      this.updateFromJson()

      // Update type selector
      if (this.hasTypeTarget) {
        this.typeTarget.value = this.config.type || ''
      }
      this.renderOptions()
    }
  }

  updateFromJson() {
    try {
      this.config = JSON.parse(this.jsonInputTarget.value || '{}') || {}
      this.syncHidden()
    } catch (e) {
      // Invalid JSON, don't update
    }
  }

  syncHidden() {
    const hasConfig = this.config.type && Object.keys(this.config).length > 0
    this.hiddenTarget.value = hasConfig ? JSON.stringify(this.config) : ''
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str || ''
    return div.innerHTML
  }
}
